<!--
 * 文件名：labelAuditList.vue
 * 描述：数据标注-任务审核列表（v26.3.3 数据标注·运营视角，F9）。
 *       09-07 定稿：FBT 前端列表页（15_V2.0 #8"FBT 新增菜单进入标注系统审核界面"的列表部分），
 *       点入审核详情嵌标注系统审核界面（micro-app，见 labelToolDetail.vue mode=audit）。
 * 数据层（09-11）：接口由 FBT 后端自建（F0，/label-task/task-page-list），
 *       数据结构照搬 fe-csbr-data-labeling 的 task-page-list（stageCode=REVIEW 视角）；
 *       F0 未就绪时走 labelTaskApi 的本地 mock（MOCK_ENABLED）。
-->
<template>
  <div class="content work_flow_wrap">
    <!-- 环节 tab（样式对齐 fe 角色 tab：白底 40px + 1px 底线，active 文字/下划线 teal）：
         REVIEW 视角列表混合全链行（服务端不按登录人分环节过滤），tab=服务端 currentStageCode 精确过滤 -->
    <el-tabs v-model="activeStage" class="stage-tabs" @tab-click="handleStageTab">
      <el-tab-pane label="初审" name="FIRST_REVIEW"></el-tab-pane>
      <el-tab-pane label="复审" name="SECOND_REVIEW"></el-tab-pane>
      <el-tab-pane label="质检" name="QC"></el-tab-pane>
      <el-tab-pane label="已完结" name="COMPLETED"></el-tab-pane>
    </el-tabs>

    <!-- 筛选区（SearchBar 组件，样式基准 productInformation，同 labelTemplateConf） -->
    <search-bar
      v-model="query"
      :conditions="conditions"
      @search="search"
      @reset="reset"
      @height-change="recalcLayout"
    >
      <!-- 核算月范围：月标签按核算月口径换算成任务周期窗口（选月M=核算月M），后端按任务周期区间重叠过滤；
           与小程序"我的审核"同口径（SearchBar 无 monthrange 类型，复杂控件走具名插槽） -->
      <template #accountMonthRange>
        <el-date-picker
          v-model="accountMonthRange"
          type="monthrange"
          value-format="yyyy-MM"
          size="mini"
          class="ipt-sty"
          range-separator="至"
          start-placeholder="开始月份"
          end-placeholder="结束月份"
          clearable
        ></el-date-picker>
      </template>
    </search-bar>

    <!-- 列表 -->
    <div class="back-color-fff content-main-box" ref="tableBox" :style="'height:' + contentMainHeight + 'px'">
      <el-table
        ref="table"
        :data="tableData"
        v-loading="tableLoading"
        element-loading-text="加载中…"
        border
        stripe
        style="width: 100%"
        :height="tableHeight"
      >
        <el-table-column label="序号" width="50" header-align="center" align="center" fixed="left">
          <template slot-scope="scope">
            {{ (query.pageIndex - 1) * query.pageSize + scope.$index + 1 }}
          </template>
        </el-table-column>
        <el-table-column prop="projectName" label="项目名称" min-width="150" show-overflow-tooltip></el-table-column>
        <el-table-column prop="taskPackageName" label="任务包名称" min-width="150" show-overflow-tooltip></el-table-column>
        <!-- 标注执行人：人级化后每行=一个执行人（task-page-list 的 assigneeName，2026-10-14 需求） -->
        <el-table-column prop="assigneeName" label="标注执行人" width="100" show-overflow-tooltip></el-table-column>
        <el-table-column label="标注类型" width="110" show-overflow-tooltip>
          <template slot-scope="scope">{{ labelTypeNameOf(scope.row.labelTypeCode) }}</template>
        </el-table-column>
        <el-table-column label="审核进度" width="110" header-align="center">
          <template slot-scope="scope">
            <div class="audit-progress">{{ stageProgressOf(scope.row) }}</div>
          </template>
        </el-table-column>
        <el-table-column label="任务状态" width="100" header-align="center" align="center">
          <template slot-scope="scope">
            <el-tag :type="stageTagOf(scope.row)" size="small">
              {{ stageTextOf(scope.row) }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="phaseStartTime" label="创建时间" width="160">
          <template slot-scope="scope">{{ scope.row.phaseStartTime || "-" }}</template>
        </el-table-column>
        <el-table-column label="操作" width="90" fixed="right" header-align="center" align="center">
          <template slot-scope="scope">
            <el-button v-if="rowStateOf(scope.row).done" type="text" @click="toAudit(scope.row)">详情</el-button>
            <el-button v-else type="text" @click="toAudit(scope.row)">开始审核</el-button>
          </template>
        </el-table-column>
        <template slot="empty">
          <span>{{ tableLoadFailed ? "加载失败，请重试" : "暂无审核任务" }}</span>
          <el-button v-if="tableLoadFailed" type="text" @click="loadList">重新加载</el-button>
        </template>
      </el-table>

      <el-pagination
        ref="pager"
        class="pt pb back-color-fff"
        background
        @size-change="handleSizeChange"
        @current-change="pageChange"
        :page-sizes="[10, 20, 50, 100]"
        :current-page="query.pageIndex"
        :page-size="query.pageSize"
        :total="total"
        layout="total, sizes, prev, pager, next, jumper"
      ></el-pagination>
    </div>
  </div>
</template>

<script>
import SearchBar from "@/components/SearchBar";
import { CONTRACT_URLS, MOCK_ENABLED, STAGE_CODES, LABEL_TYPE_LIST, buildTaskPageQuery, rowState, mockTaskPageList, labelTypeName } from "./components/labelTaskApi";

// 进审核详情是整页跳转（toAudit 用 window.location.href 保证 LS 子应用整页重建），返回时 SPA 重新启动，
// keep-alive 缓存随之失效——环节 tab 选中态改存 sessionStorage（按浏览器标签页隔离），返回重建时恢复（FOUR-3037）
const STAGE_KEY = "labelAuditList.activeStage";
const STAGE_VALUES = ["FIRST_REVIEW", "SECOND_REVIEW", "QC", "COMPLETED"];
function restoreActiveStage() {
  try {
    const v = sessionStorage.getItem(STAGE_KEY);
    return STAGE_VALUES.indexOf(v) > -1 ? v : "FIRST_REVIEW";
  } catch (e) {
    return "FIRST_REVIEW";
  }
}

export default {
  name: "labelAuditList",
  components: { SearchBar },
  data() {
    return {
      // 引导估值：el-table 以 0 起步再在 mounted 里改值会丢高度更新（表格压成 0），先给非 0 初值，挂载后 recalcLayout 实测校正
      tableHeight: window.innerHeight - 320,
      contentMainHeight: window.innerHeight - 250,
      query: {
        projectName: "",
        taskPackageName: "",
        labelTypeCode: "",
        state: "",
        currentStageCode: "",
        pageIndex: 1,
        pageSize: 10,
      },
      // 当前环节 tab（默认初审）：loadList 时写入 query.currentStageCode 驱动服务端精确过滤；初值从 sessionStorage 恢复（整页往返场景）
      activeStage: restoreActiveStage(),
      // 核算月范围筛选（["2026-08","2026-10"] 月标签）：loadList 直发 YYYYMM，后端按 tr_task.account_month 区间过滤
      accountMonthRange: null,
      tableData: [],
      total: 0,
      tableLoading: false,
      tableLoadFailed: false,
      labelTypeOptions: LABEL_TYPE_LIST,
    };
  },
  computed: {
    conditions() {
      return [
        { key: "projectName", label: "项目名称", type: "input" },
        { key: "taskPackageName", label: "任务包名称", type: "input" },
        { key: "labelTypeCode", label: "标注类型", type: "select", options: this.labelTypeOptions },
        // 低频条件收进"更多筛选"折叠行（首行固定 3 条+按钮组，避免核算月独占第二行）
        { key: "accountMonthRange", label: "核算月", type: "slot", more: true },
      ];
    },
  },
  watch: {
    // tab 切换即持久化：整页往返后 created 重建时经 restoreActiveStage 恢复（FOUR-3037）
    activeStage(v) {
      try {
        sessionStorage.setItem(STAGE_KEY, v);
      } catch (e) {
        /* 隐私模式等存储不可用时静默降级为默认初审 */
      }
    },
  },
  mounted() {
    this.recalcLayout();
    window.addEventListener("resize", this.recalcLayout);
  },
  activated() {
    // keep-alive 缓存页（路由 meta.keepAlive）：环节 tab 选中态随组件缓存保留；
    // 非首次激活（从 labelToolDetail 返回）时重查列表+重算表格高度（页面缓存但数据重新请求）。
    // 首次进入 created 已首查，_everActivated 防双查
    if (this._everActivated) {
      this.loadList();
      this.$nextTick(() => this.recalcLayout());
    }
    this._everActivated = true;
  },
  beforeDestroy() {
    window.removeEventListener("resize", this.recalcLayout);
  },
  created() {
    this.search();
  },
  methods: {
    // 高度锚点实测：以内容盒顶部为锚，免去 header/条件行等魔法数；底部留 8px；取整避免小数像素
    recalcLayout() {
      if (!this.$refs.tableBox) return;
      const top = this.$refs.tableBox.getBoundingClientRect().top;
      this.contentMainHeight = Math.floor(window.innerHeight - top - 8);
      this.tableHeight = Math.floor(this.contentMainHeight - this.$refs.pager.$el.offsetHeight - 2);
      // 兜底：el-table 的 height watcher 有首帧丢更新场景（prop 已变而内部 layout 不动），nextTick 强制重设一次，已一致时幂等
      this.$nextTick(() => {
        if (this.$refs.table) this.$refs.table.layout.setHeight(this.tableHeight);
      });
    },
    rowStateOf(row) {
      return rowState(row, STAGE_CODES.REVIEW);
    },
    // 环节化状态文案：一个列表混三环节+完结（REVIEW 视角=租户级全链行），原"进行中/已完成"看不出该谁操作；
    // 未整体提交前始终显示本环节"待初审/待复审/待质检"——不因逐份审完（reviewCanSubmit）跳"待流转"
    //（2026-10-16 用户口径）
    stageTextOf(row) {
      const c = row.currentStageCode;
      if (c === "COMPLETED") return "已完结";
      if (row.rejectedFlag === "Y") return "待整改";
      if (c === "QC") return "待质检";
      if (c === "SECOND_REVIEW") return "待复审";
      if (c === "FIRST_REVIEW") return "待初审";
      return "进行中";
    },
    stageTagOf(row) {
      if (row.currentStageCode === "COMPLETED") return "success";
      return row.rejectedFlag === "Y" ? "danger" : "warning";
    },
    // 审核进度（2026-10-16 用户口径）：仅 x/y——当前环节已处理数/总数
    //（初审/复审/质检=reviewProcessedCount 实时值：有效通过+未整改驳回，随每份审核变化、与提交门槛同口径；
    // 弃用 ext 列 reviewTaskNum/qcTaskNum/acceptanceTaskNum——其只含有效通过数，驳回不计数进度会停 0/2；
    // 已完结=验收有效 PASS 数/总量，抽检场景为样本通过数）；不再展示下方明细行与提示行
    stageProgressOf(row) {
      const total = row.itemTotalNum || 0;
      const c = row.currentStageCode;
      if (c === "COMPLETED") {
        const accepted = row.acceptanceTaskNum === null || row.acceptanceTaskNum === undefined ? 0 : row.acceptanceTaskNum;
        return accepted + "/" + total;
      }
      const reviewing = c === "FIRST_REVIEW" || c === "SECOND_REVIEW" || c === "QC";
      const num = reviewing ? row.reviewProcessedCount : null;
      return (num === null || num === undefined ? 0 : num) + "/" + total;
    },
    labelTypeNameOf(code) {
      return labelTypeName(code);
    },
    search() {
      this.query.pageIndex = 1;
      this.loadList();
    },
    // 环节 tab 切换：重置页码重查（loadList 统一同步 query.currentStageCode）
    handleStageTab() {
      this.query.pageIndex = 1;
      this.loadList();
    },
    reset() {
      this.query = { projectName: "", taskPackageName: "", labelTypeCode: "", state: "", currentStageCode: this.activeStage, pageIndex: 1, pageSize: 10 };
      this.accountMonthRange = null;
      this.loadList();
    },
    // 核算月范围（2026-10-11 起）：直发 YYYYMM 参数（loadList 内组装），搜索/清空/翻页自动携带；
    // reset() 置 null 后 loadList 不再下发该参即不过滤
    loadList() {
      this.query.currentStageCode = this.activeStage;
      this.tableLoading = true;
      this.tableLoadFailed = false;
      // 核算月范围（2026-10-11 口径对齐任务执行列表）：直发 YYYYMM（"2026-09"→"202609"），
      // 由后端按 tr_task.account_month 字符串区间过滤；原 getAccountMonthFullArea 换任务周期窗口的
      // 重叠口径与核算月不符、表现为"未生效"，本次移除
      const accountMonthParams =
        this.accountMonthRange && this.accountMonthRange.length === 2
          ? {
              startAccountMonth: this.accountMonthRange[0].replace(/-/g, ""),
              endAccountMonth: this.accountMonthRange[1].replace(/-/g, ""),
            }
          : {};
      // 审核 tab（初审/复审/质检）排除停驻整改行：rejected_flag=Y=审核整包提交已定稿驳回、转待整改
      // （待整改列表/移动端待整改承接），不属审核人待办；未提交的驳回中行仍留 tab 供继续处理。
      // sales mapper 有 'N' 排除分支配套，旧后端无该分支时忽略此参数无害；已完结 tab 不受影响
      const extra = this.activeStage === "COMPLETED" ? {} : { rejectedFlag: "N" };
      const body = buildTaskPageQuery(this.query, STAGE_CODES.REVIEW, { ...extra, ...accountMonthParams });
      const done = (data) => {
        this.tableLoading = false;
        this.tableData = data.records || [];
        this.total = data.totalRows || 0;
      };
      const fail = () => {
        this.tableLoading = false;
        this.tableLoadFailed = true;
        this.tableData = [];
        this.total = 0;
      };
      if (MOCK_ENABLED) {
        mockTaskPageList(body).then(done).catch(fail);
        return;
      }
      this.api
        .postJson(`/${serverConfig.msSalesPerformService}${CONTRACT_URLS.taskPageList}`, body)
        .then((res) => {
          if (res.data.code == serverConfig.REQUIRE_SUCCESS_CODE) done(res.data.data || {});
          else fail();
        })
        .catch(fail);
    },
    pageChange(page) {
      this.query.pageIndex = page;
      this.loadList();
    },
    handleSizeChange(size) {
      this.query.pageSize = size;
      this.loadList();
    },
    toAudit(row) {
      // 整页导航进入详情：micro-app 子应用按 name keep-alive 复用（iframe 沙箱档禁 v-if 原地重挂），
      // SPA 跳转会带入上一页（整改/其他任务）的子应用实例（残留旧 mode/旧任务列表）——整页加载保证重建
      const { href } = this.$router.resolve({
        path: "/labelToolDetail",
        query: {
          mode: "audit",
          projectId: row.labelStudioProjectId,
          taskPackageGuid: row.guid,
          taskId: row.labelStudioTaskId || row.firstLabelStudioTaskId || "",
          stageRuleCode: row.stageRuleCode || "",
          allowModifyFlag: row.allowModifyFlag || "",
          // 行阶段码（位置映射mode用）：FIRST_REVIEW/SECOND_REVIEW/QC/COMPLETED（完结行按链尾QC格展示）
          stageCode: row.currentStageCode || "",
          projectName: row.projectName,
          taskTitle: row.taskPackageName,
        },
      });
      window.location.href = href;
    },
  },
};
</script>

<style scoped>
.audit-progress {
  font-size: 14px;
  color: #606266;
}
/* 环节 tab：el-tabs 主题色覆盖（对齐 fe 角色 tab 实测样式——白底 40px、1px #D9D9D9 底线、
   active 文字+2px 下划线 teal #4FA1A4、非 active #606266、tab 项 padding 0 20px） */
.stage-tabs /deep/ .el-tabs__header {
  margin: 0 0 8px;
  background: #ffffff;
}
.stage-tabs /deep/ .el-tabs__nav-scroll {
  padding-left: 16px;
}
.stage-tabs /deep/ .el-tabs__nav-wrap::after {
  height: 1px;
  background-color: #d9d9d9;
}
.stage-tabs /deep/ .el-tabs__item {
  height: 40px;
  line-height: 40px;
  padding: 0 20px;
  font-size: 14px;
  color: #606266;
}
.stage-tabs /deep/ .el-tabs__item.is-active {
  color: #4fa1a4;
}
.stage-tabs /deep/ .el-tabs__active-bar {
  height: 2px;
  background-color: #4fa1a4;
}
</style>
